export type TenantRoleInfo = {
  id: string;
  key: string;
  name: string;
};

export type SessionMe = {
  user: {
    id: string;
    email: string;
    name: string | null;
    mustChangePassword?: boolean;
    role: TenantRoleInfo | null;
    legacyRole?: string;
  };
  organization: { id: string; name: string; slug: string; logoUrl?: string | null };
  permissions: string[];
};

/** `manage` cubre write/read; `write` cubre read. */
export function permissionSatisfies(granted: Set<string> | string[], required: string): boolean {
  const g = granted instanceof Set ? granted : new Set(granted);
  if (g.has(required)) return true;
  const parts = required.split('.');
  if (parts.length !== 3) return false;
  const [surface, module, action] = parts;
  if (action === 'read') {
    if (g.has(`${surface}.${module}.write`)) return true;
    if (g.has(`${surface}.${module}.manage`)) return true;
  }
  if (action === 'write' && g.has(`${surface}.${module}.manage`)) return true;
  return false;
}

export function hasPermission(granted: Set<string> | string[], required: string | string[]): boolean {
  const list = Array.isArray(required) ? required : [required];
  return list.some((p) => permissionSatisfies(granted, p));
}

export function canRead(granted: Set<string> | string[], module: string, surface: 'web' | 'pwa' = 'web'): boolean {
  return permissionSatisfies(granted, `${surface}.${module}.read`);
}

export function canWrite(granted: Set<string> | string[], module: string, surface: 'web' | 'pwa' = 'web'): boolean {
  return (
    permissionSatisfies(granted, `${surface}.${module}.write`) ||
    permissionSatisfies(granted, `${surface}.${module}.manage`)
  );
}

/** Permiso mínimo de lectura por ruta de consola `/app/...`. */
export const WEB_ROUTE_READ_PERMISSION: Record<string, string> = {
  '/app': 'web.dashboard.read',
  '/app/agents': 'web.agents.read',
  '/app/plugins': 'web.agents.read',
  '/app/workflows': 'web.workflows.read',
  '/app/integrations': 'web.integrations.read',
  '/app/plantillas': 'web.agents.read',
  '/app/settings/notifications': 'web.notification_settings.read',
  '/app/settings/brand': 'web.dashboard.read',
  '/app/conversations': 'web.conversations.read',
  '/app/learnings': 'web.learnings.read',
  '/app/rules': 'web.learnings.read',
  '/app/handoff': 'web.learnings.read',
  '/app/message-analytics': 'web.message_analytics.read',
  '/app/docs': 'web.docs.read',
  '/app/test': 'web.test_demo.read',
  '/app/calendario': 'web.calendar.read',
  '/app/audit': 'web.agents.read',
  '/app/lista-negra': 'web.conversations.read',
  '/app/conectores-ads': 'web.integrations.read',
  '/app/notifications': 'web.notifications.read',
};

export function readPermissionForAppPath(pathname: string): string | null {
  if (!pathname.startsWith('/app')) return null;
  if (/^\/app\/agents\/[^/]+\/calendar(\/|$)/.test(pathname)) return 'web.calendar.read';
  const entries = Object.entries(WEB_ROUTE_READ_PERMISSION).sort((a, b) => b[0].length - a[0].length);
  for (const [prefix, perm] of entries) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return perm;
  }
  return 'web.dashboard.read';
}

export const PWA_ROUTE_READ_PERMISSION: Record<string, string> = {
  '/': 'pwa.home.read',
  '/notifications': 'pwa.notifications.read',
  '/conversations': 'pwa.conversations.read',
  '/calendar': 'pwa.calendar.read',
};
